// src/components/shops/SocialBrewBadge.tsx
//
// The strip that sits at the foot of every marketing template.
//
// It is deliberately a badge rather than a watermark. "Find us on Social Brew"
// advertises us and a shop paying $99 a month eventually resents carrying it.
// "Rate us on Social Brew", shown next to the shop's own score, advertises
// *them* — it drives their rating count, which improves their listing, and the
// score is social proof they want on the post. Same pixels, and shops ask for
// it rather than asking to remove it.
//
// For that reason it appears on every tier. Growth shops make the best-looking
// posts and have the biggest followings; stripping our mark from exactly those
// would remove the channel where it works hardest.

type Tone = 'light' | 'dark'

interface Props {
  /** Shop's average fill, 0–100. Omitted or 0 hides the score half. */
  rating?: number | null
  /** How many ratings the score is based on. Hidden below `minRatings`. */
  ratingCount?: number | null
  /** `light` for cream templates, `dark` for espresso ones. */
  tone?: Tone
  /** Scales the whole strip for export canvases, where 1 is the on-screen size. */
  scale?: number
}

// The real mark, in both weights. Unlike icon-512.png — which the rest of the
// app composites with mix-blend-mode: multiply because it carries a white
// background — these two are RGBA with genuine transparency, so they sit on any
// ground without a blend mode and without needing an isolation boundary.
const MARK_DARK = '/sb-logo.png'   // dark cup, for cream templates
const MARK_LIGHT = '/sb-logo-light.png' // cream cup, for espresso templates

// A score built on one or two ratings flatters nobody and invites doubt, so the
// strip falls back to the plain call to action until there is enough behind it.
const MIN_RATINGS = 3

export default function SocialBrewBadge({
  rating,
  ratingCount,
  tone = 'light',
  scale = 1,
}: Props) {
  const showScore =
    typeof rating === 'number' && rating > 0 &&
    typeof ratingCount === 'number' && ratingCount >= MIN_RATINGS

  const ink = tone === 'dark' ? '#f0e2cb' : '#241810'
  const soft = tone === 'dark' ? 'rgba(240,226,203,0.62)' : 'rgba(110,80,56,0.85)'
  const rule = tone === 'dark' ? 'rgba(240,226,203,0.18)' : 'rgba(200,133,58,0.22)'
  const mark = tone === 'dark' ? MARK_LIGHT : MARK_DARK

  const px = (n: number) => `${n * scale}px`

  return (
    <div
      className="flex items-center justify-between"
      style={{
        borderTop: `1px solid ${rule}`,
        paddingTop: px(9),
        paddingLeft: px(2),
        paddingRight: px(2),
        gap: px(10),
      }}
    >
      {/* Call to action, with the mug to the right of the wordmark */}
      <div className="flex items-center" style={{ gap: px(5) }}>
        <span
          style={{
            fontSize: px(8),
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: soft,
            fontWeight: 600,
          }}
        >
          Rate us on
        </span>
        <span
          className="font-display"
          style={{ fontSize: px(13), fontWeight: 700, color: ink, letterSpacing: '-0.01em' }}
        >
          Social Brew
        </span>
        <img
          src={mark}
          alt=""
          width={16 * scale}
          height={16 * scale}
          style={{ display: 'block', flexShrink: 0 }}
        />
      </div>

      {showScore && (
        <div className="flex items-baseline flex-shrink-0" style={{ gap: px(4) }}>
          <span
            className="font-display"
            style={{ fontSize: px(14), fontWeight: 700, color: ink, lineHeight: 1 }}
          >
            {Math.round(rating as number)}%
          </span>
          <span style={{ fontSize: px(8), color: soft, letterSpacing: '0.04em' }}>
            {ratingCount} ratings
          </span>
        </div>
      )}
    </div>
  )
}
